import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import doorsData from "../data/doorsData.json";
import { useLocation } from "react-router-dom";
import Door3DViewer from "../Components/Door3DViewer";
import SEO from "../Components/SEO";

const Doors = () => {
    const [activeFilter, setActiveFilter] = useState("All");
    // eslint-disable-next-line no-unused-vars
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedDoor, setSelectedDoor] = useState(null);
    const [fullscreenImage, setFullscreenImage] = useState(null); // mobile fullscreen
    // eslint-disable-next-line no-unused-vars
    const [mobileGrid, setMobileGrid] = useState("2x2");
    const [zoomLevel, setZoomLevel] = useState(1);
    const [doorAnimated, setDoorAnimated] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [viewMode, setViewMode] = useState("2d"); // "2d" | "3d"
    const dropdownRef = useRef(null);

    // Pan state
    const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
    const isPanning = useRef(false);
    const lastPointer = useRef({ x: 0, y: 0 });
    const imageWrapperRef = useRef(null);

    // Handle click outside custom dropdown on mobile
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        if (showDropdown) {
            document.addEventListener("mousedown", handleClickOutside);
            document.addEventListener("touchstart", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [showDropdown]);

    const { search } = useLocation();
    
    useEffect(() => {
        const params = new URLSearchParams(search);
        const categoryParam = params.get("category");
        if (categoryParam) {
            setActiveFilter(categoryParam);
            window.scrollTo(0, 0);
        }
    }, [search]);

useEffect(() => {
  if (selectedDoor) {
    document.body.classList.add("door-modal-open");
  } else {
    document.body.classList.remove("door-modal-open");
  }

  return () => {
    document.body.classList.remove("door-modal-open");
  };
}, [selectedDoor]);

    const [viewStatus, setViewStatus] = useState({});

    const filteredData = useMemo(() => {
        return doorsData.categories
            .filter(cat => activeFilter === "All" || cat.type === activeFilter)
            .map(category => ({
                ...category,
                doors: category.doors.filter(door =>
                    door.code.toLowerCase().includes(searchQuery.toLowerCase())
                )
            }))
            .filter(category => category.doors.length > 0);
    }, [activeFilter, searchQuery]);




    const closeModal = useCallback(() => {
        if (viewMode === "3d") {
            window.history.go(-2);
        } else {
            window.history.go(-1);
        }
    }, [viewMode]);

    useEffect(() => {
        const handleEsc = (event) => {
            if (event.keyCode === 27) closeModal();
        };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [closeModal]);

    // Reset zoom, pan & trigger door animation when modal opens
    // Also push a history entry so the browser back button closes the modal
    useEffect(() => {
        if (selectedDoor) {
            setZoomLevel(1);
            setPanOffset({ x: 0, y: 0 });
            setDoorAnimated(false);
            const t = setTimeout(() => setDoorAnimated(true), 60);
            // Push a dummy hash so back button has somewhere to go
            window.history.pushState({ modalOpen: true }, "");
            return () => clearTimeout(t);
        }
    }, [selectedDoor]);

    // Push state for 3D view mode to allow back navigation to 2D
    useEffect(() => {
        if (selectedDoor && viewMode === "3d") {
            window.history.pushState({ viewMode3D: true }, "");
        }
    }, [viewMode, selectedDoor]);

    // Listen for browser back button — close modal instead of navigating away
    useEffect(() => {
        const handlePopState = (e) => {
            if (fullscreenImage) {
                setFullscreenImage(null);
                e.preventDefault();
            } else if (viewMode === "3d") {
                setViewMode("2d");
                e.preventDefault();
            } else if (selectedDoor) {
                setSelectedDoor(null);
                setViewMode("2d"); // Reset viewMode to 2d when closing
                e.preventDefault();
            }
        };
        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, [selectedDoor, fullscreenImage, viewMode]);

    // Push history + lock body when fullscreen opens
    useEffect(() => {
        if (fullscreenImage) {
            window.history.pushState({ fullscreen: true }, "");
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = selectedDoor ? "hidden" : "";
        }
    }, [fullscreenImage, selectedDoor]);

    // Reset pan when zoom goes back to 1
    useEffect(() => {
        if (zoomLevel === 1) {
            setPanOffset({ x: 0, y: 0 });
        }
    }, [zoomLevel]);



    useEffect(() => {
  const categoriesToPreload =
    activeFilter === "All"
      ? doorsData.categories
      : doorsData.categories.filter((cat) => cat.type === activeFilter);

  categoriesToPreload.forEach((category) => {
    category.doors.slice(5, 10).forEach((door) => {
      const img = new Image();
      img.src = door.image;
    });
  });
}, [activeFilter]);

    // Lock body scroll when modal open
    useEffect(() => {
        if (selectedDoor) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => { document.body.style.overflow = ""; };
    }, [selectedDoor]);

    const toggleViewMore = (categoryType) => {
        setViewStatus(prev => ({
            ...prev,
            [categoryType]: (prev[categoryType] || 0) + 1
        }));
    };

    const handleZoomIn = (e) => {
        e.stopPropagation();
        setZoomLevel(prev => Math.min(prev + 0.25, 3));
    };
    const handleZoomOut = (e) => {
        e.stopPropagation();
        setZoomLevel(prev => {
            const next = Math.max(prev - 0.25, 0.5);
            if (next === 1) setPanOffset({ x: 0, y: 0 });
            return next;
        });
    };
    const handleZoomReset = (e) => {
        e.stopPropagation();
        setZoomLevel(1);
        setPanOffset({ x: 0, y: 0 });
    };

    // ── Pan handlers (mouse) ──
    const onMouseDown = useCallback((e) => {
        if (zoomLevel <= 1) return;
        e.preventDefault();
        isPanning.current = true;
        lastPointer.current = { x: e.clientX, y: e.clientY };
    }, [zoomLevel]);

    const onMouseMove = useCallback((e) => {
        if (!isPanning.current) return;
        const dx = e.clientX - lastPointer.current.x;
        const dy = e.clientY - lastPointer.current.y;
        lastPointer.current = { x: e.clientX, y: e.clientY };
        setPanOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
    }, []);

    const onMouseUp = useCallback(() => {
        isPanning.current = false;
    }, []);

    // ── Pan handlers (touch) ──
    const onTouchStart = useCallback((e) => {
        if (zoomLevel <= 1) return;
        if (e.touches.length === 1) {
            isPanning.current = true;
            lastPointer.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }
    }, [zoomLevel]);

    const onTouchMove = useCallback((e) => {
        if (!isPanning.current || e.touches.length !== 1) return;
        e.preventDefault();
        const dx = e.touches[0].clientX - lastPointer.current.x;
        const dy = e.touches[0].clientY - lastPointer.current.y;
        lastPointer.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        setPanOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
    }, []);

    const onTouchEnd = useCallback(() => {
        isPanning.current = false;
    }, []);

    // Attach touch listeners with passive:false to allow preventDefault
    useEffect(() => {
        const el = imageWrapperRef.current;
        if (!el) return;
        el.addEventListener("touchstart", onTouchStart, { passive: true });
        el.addEventListener("touchmove", onTouchMove, { passive: false });
        el.addEventListener("touchend", onTouchEnd, { passive: true });
        return () => {
            el.removeEventListener("touchstart", onTouchStart);
            el.removeEventListener("touchmove", onTouchMove);
            el.removeEventListener("touchend", onTouchEnd);
        };
    }, [onTouchStart, onTouchMove, onTouchEnd]);


    const [showFullDescription, setShowFullDescription] = useState(false);

const modalDescription =
    selectedDoor?.categoryDescription ||
    selectedDoor?.description ||
    "Crafted with precision to deliver unparalleled elegance and durability for your living spaces.";



    // Color Palette
    const colors = {
        bg: "#f9f1e7",
        gold: "#D4AF37",
        darkBrown: "#5D4037",
        textLight: "#888"
    };

    return (
        <div style={{
            backgroundColor: colors.bg,
            minHeight: "100vh",
            fontFamily: "'Poppins', sans-serif"
        }} className="page-container">
            <SEO
                title={activeFilter === "All" ? "Complete Doors Catalogue (2D and 3D Interactive Viewer)" : `${activeFilter} Doors in Rajahmundry`}
                description={`Browse our premium collection of ${activeFilter === "All" ? "teak wood, WPC, Sunmica, and prelaminated" : activeFilter} doors at Sree Raghavendra Rajahmundry showroom. Interactive 3D preview and custom sizes available.`}
                canonicalUrl={activeFilter === "All" ? "/doors" : `/doors?category=${activeFilter}`}
                keywords="doors catalogue rajahmundry, 3d door viewer, teak doors rajamahendravaram, wpc doors east godavari, sunmica door catalog morampudi"
                ogImage="https://raghavendradoorsandhardware.in/assets/prelaminated.png"
            />


            {/* PAGE TITLE (NOT STICKY) */}
            <div style={{ textAlign: "center", padding: "20px 20px 20px 20px" }}>
                <h1 className="page-title" style={{
                    color: colors.darkBrown,
                    fontWeight: "700",
                    margin: "0 0 10px 0",
                    letterSpacing: "0.5px"
                }}>
                    Premium Door Gallery in Rajahmundry
                </h1>
                <p style={{ color: "#7a6b5d", fontSize: "1.1rem", margin: 0 }}>
                    Find the perfect door for your exquisite interior.
                </p>
            </div>

            {/* ══════════════════════════════════════════
                UNIFIED STICKY CONTROL BAR (Commented out / Hidden)
            ══════════════════════════════════════════ */}
            {/*
            <div className="sticky-control-bar">
                <div className="unified-controls-row">

                    {/* Search Bar }
                    <div className="search-wrapper">
                        <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search by code..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button className="search-clear-btn" onClick={() => setSearchQuery("")}>
                                <svg viewBox="0 0 14 14" fill="none" width="10" height="10"><path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                            </button>
                        )}
                    </div>

                    {/* Filter Pills — desktop }
                    <div className="filter-scroll-container desktop-filters">
                        <div className="filter-track">
                            {["All", ...doorsData.categories.map(c => c.type)].map((cat) => (
                                <button
                                    key={cat}
                                    className={`filter-btn ${activeFilter === cat ? "active" : ""}`}
                                    onClick={() => setActiveFilter(cat)}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Mobile: Category Dropdown }
                    <div className="mobile-dropdown-wrapper mobile-only" ref={dropdownRef}>
                        <button
                            className="mobile-dropdown-toggle"
                            onClick={() => setShowDropdown(!showDropdown)}
                        >
                            <span>{activeFilter}</span>
                            <svg 
                                className={`dropdown-arrow ${showDropdown ? "open" : ""}`} 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2.5" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                            >
                                <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                        </button>
                        
                        {showDropdown && (
                            <div className="mobile-dropdown-menu">
                                {["All", ...doorsData.categories.map(c => c.type)].map((cat) => (
                                    <div
                                        key={cat}
                                        className={`mobile-dropdown-item ${activeFilter === cat ? "active" : ""}`}
                                        onClick={() => {
                                            setActiveFilter(cat);
                                            setShowDropdown(false);
                                            window.scrollTo({ top: 0, behavior: "smooth" });
                                        }}
                                    >
                                        {cat}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Mobile: Grid Toggle }
                    <div className="grid-toggle-wrapper mobile-only">
                        <button
                            className={`grid-toggle-btn ${mobileGrid === "2x2" ? "active" : ""}`}
                            onClick={() => setMobileGrid("2x2")}
                            title="2 columns"
                        >
                            <svg viewBox="0 0 20 20" width="15" height="15" fill="currentColor">
                                <rect x="1" y="1" width="8" height="8" rx="1.5"/>
                                <rect x="11" y="1" width="8" height="8" rx="1.5"/>
                                <rect x="1" y="11" width="8" height="8" rx="1.5"/>
                                <rect x="11" y="11" width="8" height="8" rx="1.5"/>
                            </svg>
                            <span>2×2</span>
                        </button>
                        <button
                            className={`grid-toggle-btn ${mobileGrid === "4x4" ? "active" : ""}`}
                            onClick={() => setMobileGrid("4x4")}
                            title="4 columns"
                        >
                            <svg viewBox="0 0 20 20" width="15" height="15" fill="currentColor">
                                <rect x="0.5" y="0.5" width="4" height="4" rx="1"/>
                                <rect x="5.5" y="0.5" width="4" height="4" rx="1"/>
                                <rect x="10.5" y="0.5" width="4" height="4" rx="1"/>
                                <rect x="15.5" y="0.5" width="4" height="4" rx="1"/>
                                <rect x="0.5" y="5.5" width="4" height="4" rx="1"/>
                                <rect x="5.5" y="5.5" width="4" height="4" rx="1"/>
                                <rect x="10.5" y="5.5" width="4" height="4" rx="1"/>
                                <rect x="15.5" y="5.5" width="4" height="4" rx="1"/>
                                <rect x="0.5" y="10.5" width="4" height="4" rx="1"/>
                                <rect x="5.5" y="10.5" width="4" height="4" rx="1"/>
                                <rect x="10.5" y="10.5" width="4" height="4" rx="1"/>
                                <rect x="15.5" y="10.5" width="4" height="4" rx="1"/>
                                <rect x="0.5" y="15.5" width="4" height="4" rx="1"/>
                                <rect x="5.5" y="15.5" width="4" height="4" rx="1"/>
                                <rect x="10.5" y="15.5" width="4" height="4" rx="1"/>
                                <rect x="15.5" y="15.5" width="4" height="4" rx="1"/>
                            </svg>
                            <span>4×4</span>
                        </button>
                    </div>

                </div>
            </div>
            */}

            {/* MAIN CONTENT */}
            <div className="content-area" style={{ maxWidth: "1400px", margin: "0 auto", padding: "20px 20px 5px 20px" }}>
                {filteredData.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "80px 20px", color: "#888" }}>
                        <h3 style={{ fontSize: "1.5rem", color: colors.darkBrown }}>No doors found</h3>
                        <p>Try adjusting your search or selecting a different category.</p>
                    </div>
                ) : (
                    filteredData.map((category) => {
                        const status = viewStatus[category.type] || 0;
                        const initialLimit = mobileGrid === "4x4" ? 8 : 5;
                        const increment = mobileGrid === "4x4" ? 8 : 10;
                        const displayLimit = status === 0
                            ? Math.min(initialLimit, category.doors.length)
                            : Math.min(initialLimit + status * increment, category.doors.length);

                        return (
                            <div key={category.type} style={{ marginBottom: "60px" }}>
                                <h2 className="section-title" style={{
                                    color: colors.darkBrown,
                                    borderLeft: `6px solid ${colors.gold}`,
                                    paddingLeft: "15px",
                                    marginBottom: "30px",
                                    fontWeight: "600"
                                }}>
                                    {category.type} Doors
                                </h2>

                                <div className={`door-grid ${mobileGrid === "4x4" ? "mobile-grid-4" : "mobile-grid-2"}`}>
                                    {category.doors.slice(0, displayLimit).map((door, i) => (
                                        <div
                                            key={i}
                                            className="door-card"
onClick={() => {
  setViewMode("2d");
  setSelectedDoor({
    ...door,
    type: category.type,
    categoryDescription: category.description,
  });
}}
                                            style={{
                                                backgroundColor: "#fff",
                                                borderRadius: "15px",
                                                padding: "15px",
                                                boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
                                                cursor: "pointer",
                                                transition: "all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1)",
                                                textAlign: "center",
                                                border: "1px solid #f0f0f0"
                                            }}
                                        >
                                            <div style={{ overflow: "hidden", borderRadius: "10px", marginBottom: "15px" }}>
                                                <img
                                                  src={door.image}
                                                  alt={door.code}
                                                  className="door-img"
                                                  loading="lazy"
                                                  decoding="async"
                                                  style={{ width: "100%", objectFit: "contain" }}
                                                />
                                            </div>
                                            <h4 style={{ margin: "5px 0", color: "#333", fontSize: "18px", fontWeight: "600" }}>{door.code}</h4>
                                        </div>
                                    ))}

                                    {category.doors.length > displayLimit && mobileGrid === "2x2" && (
                                         <div className="mobile-view-more-grid-item">
                                             <button
                                                 className="view-more-btn mobile-grid-view-more-btn"
                                                 onClick={() => toggleViewMore(category.type)}
                                             >
                                                 <span>View More {category.type} Doors</span>
                                                 <svg 
                                                     className="view-more-arrow-icon" 
                                                     viewBox="0 0 24 24" 
                                                     fill="none" 
                                                     stroke="currentColor" 
                                                     strokeWidth="2.5" 
                                                     strokeLinecap="round" 
                                                     strokeLinejoin="round"
                                                     style={{ width: "20px", height: "20px" }}
                                                 >
                                                     <line x1="12" y1="5" x2="12" y2="19"></line>
                                                     <polyline points="19 12 12 19 5 12"></polyline>
                                                 </svg>
                                             </button>
                                         </div>
                                     )}
                                </div>

                                {category.doors.length > displayLimit && (
                                    <div className={`desktop-view-more-container ${mobileGrid === "4x4" ? "show-on-mobile-4x4" : "hide-on-mobile-2x2"}`} style={{ textAlign: "center", marginTop: "40px" }}>
                                        <button
                                            className="view-more-btn"
                                            onClick={() => toggleViewMore(category.type)}
                                        >
                                            View More {category.type} Doors
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* ══════════════════════════════════════════
                FULLSCREEN MODAL
                • Door open animation
                • Zoom + Pan (drag on desktop, touch on mobile)
                • Close button fixed — always visible
            ══════════════════════════════════════════ */}
            {selectedDoor && (
                <div
                    className="modal-overlay"
                    onClick={closeModal}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseUp}
                >
                    {/* ── Close button: fixed top-right, always on top ── */}
                    <button
                        className="modal-close-btn"
                        onClick={closeModal}
                    >
                        <svg width="18" height="18" viewBox="0 0 14 14" fill="none">
                            <path d="M13 1L1 13M1 1L13 13" stroke="#333" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </button>

                    <div
                        className={`modal-content-container ${viewMode === "3d" ? "modal-mode-3d" : ""}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {viewMode === "2d" ? (
                            <>
                                {/* Image Section */}
                                <div className="image-section" style={{
                                    display: "flex",
                                    flexDirection: "column"
                                }}>

                            {/* Door animation wrapper */}
                            <div className="door-perspective-wrapper">
                                <div className={`door-open-panel ${doorAnimated ? "door-opened" : ""}`}>
                                    {/* Pan + zoom wrapper */}
                                    <div
                                        ref={imageWrapperRef}
                                        className="image-pan-wrapper"
                                        style={{
                                            cursor: zoomLevel > 1 ? (isPanning.current ? "grabbing" : "grab") : "default",
                                        }}
                                        onMouseDown={onMouseDown}
                                    >
                                        <img
                                            className="modal-image"
                                            src={selectedDoor.image}
                                            alt={selectedDoor.code}
                                            draggable={false}
                                            onClick={() => setFullscreenImage(selectedDoor.image)}
                                            style={{
                                                transform: `scale(${zoomLevel}) translate(${panOffset.x / zoomLevel}px, ${panOffset.y / zoomLevel}px)`,
                                                transition: isPanning.current ? "none" : "transform 0.25s ease",
                                                transformOrigin: "center center",
                                                display: "block",
                                                imageRendering: "auto",
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                                    {/* Zoom + 3D View Toggles Wrapper */}
                                    <div className="modal-unified-controls-row" onClick={(e) => e.stopPropagation()}>
                                        {/* Zoom Controls */}
                                        <div className="zoom-controls" style={{ margin: 0 }}>
                                            <button className="zoom-btn" onClick={handleZoomOut} title="Zoom Out" disabled={zoomLevel <= 0.5}>
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                                                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/>
                                                </svg>
                                            </button>
                                            <button className="zoom-reset-btn" onClick={handleZoomReset} title="Reset Zoom">
                                                <span>{Math.round(zoomLevel * 100)}%</span>
                                            </button>
                                            <button className="zoom-btn" onClick={handleZoomIn} title="Zoom In" disabled={zoomLevel >= 3}>
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                                                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
                                                </svg>
                                            </button>
                                        </div>

                                        {/* 3D Structure Switcher Button */}
                                         {/* <button
                                             className="zoom-3d-toggle-btn"
                                             onClick={() => setViewMode("3d")}
                                             title="View in 3D Structure"
                                         >
                                             <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                 <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                                                 <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                                                 <line x1="12" y1="22.08" x2="12" y2="12" />
                                             </svg>
                                             <span>3D View</span>
                                         </button> */}
                                    </div>
                                </div>

                        {/* Tap hint — mobile only */}
                        <p className="doors-tap-hint">Tap image to view fullscreen</p>

                        {/* Details Section */}
                        <div className="details-section" style={{ flex: "1", display: "flex", flexDirection: "column" }}>
                            {/* ── Fixed header: never scrolls away ── */}
                            <div className="details-header">
                                <span style={{ color: colors.gold, fontWeight: "800", letterSpacing: "2px", fontSize: "0.85rem", textTransform: "uppercase" }}>PREMIUM SERIES</span>
                                <h2
                                        className="modal-title"
                                        style={{
                                            margin: "10px 0",
                                            lineHeight: "1.3",
                                            fontSize: "2.5rem"
                                        }}
                                        >
                                        <span style={{ color: "#1a1a1a", fontWeight: 600 }}>
                                            Type:
                                        </span>{" "}
                                        <span style={{ color: colors.gold, fontWeight: 700 }}>
                                            {selectedDoor.type}
                                        </span>

                                        <br />

                                        <span style={{ color: "#1a1a1a", fontWeight: 600 }}>
                                            Model:
                                        </span>{" "}
                                        <span style={{ color: "#8B4513", fontWeight: 700 }}>
                                            {selectedDoor.code}
                                        </span>
                                        </h2>
                                <div style={{ width: "60px", height: "5px", backgroundColor: colors.gold, margin: "15px 0 0 0", borderRadius: "5px" }}></div>
                            </div>

                            {/* ── Scrollable body: description + sizes + button ── */}
                            <div className="details-body">
                         <div style={{ marginBottom: "30px" }}>
    <p
        className={`door-description ${showFullDescription ? "expanded" : ""}`}
        style={{
            color: "#666",
            lineHeight: "1.8",
            fontSize: "1rem",
            overflow: "hidden",
            transition: "all 0.4s ease"
        }}
    >
        {modalDescription}
    </p>

    {modalDescription.length > 100 && (
        <button
            onClick={() => setShowFullDescription(!showFullDescription)}
            style={{
                margin: 0,
                background: "none",
                border: "none",
                color: colors.gold,
                fontWeight: "700",
                cursor: "pointer",
                padding: 0,
                fontSize: "0.95rem"
            }}
        >
            {showFullDescription ? "View Less" : "View More"}
        </button>
    )}
</div>

                            <div className="modal-sizes-box" style={{ background: "#f9f9f9", borderRadius: "15px", marginBottom: "30px", padding: "20px" }}>
                                <p style={{ color: "#999", margin: "0 0 5px 0", fontSize: "0.9rem" }}>Available Sizes</p>
                                <p style={{ color: "#1a1a1a", margin: 0, fontWeight: "700", fontSize: "1.1rem" }}>{doorsData.doorSizes.join(", ")}</p>
                            </div>
                           <a
  href={`https://wa.me/918374771149?text=${encodeURIComponent(
    `Hi, I would like to get a quote for this door.

Door Type: ${selectedDoor.type}
Door Model: ${selectedDoor.code}

Please share the price and available details.`
  )}`}
  target="_blank"
  rel="noopener noreferrer"
  className="quote-btn"
  style={{
    background: colors.gold,
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    fontWeight: "700",
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    padding: "16px 20px",
    transition: "all 0.3s ease",
    boxShadow: "0 8px 20px rgba(212,175,55,0.25)"
  }}
>
  <i className="fa-brands fa-whatsapp"></i>
  Get Instant Quote
</a>
                            </div>{/* end details-body */}
                        </div>{/* end details-section */}
                    </>
                ) : (
                    <Door3DViewer
                        doorImage={selectedDoor.image}
                        categoryType={selectedDoor.type}
                        onToggle2D={() => window.history.back()}
                        colors={colors}
                        selectedDoor={selectedDoor}
                    />
                )}
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════
                FULLSCREEN IMAGE VIEWER (mobile)
            ══════════════════════════════════════════ */}
            {fullscreenImage && (
                <div
                    className="doors-fullscreen-overlay"
                    onClick={() => setFullscreenImage(null)}
                >
                    {/* Close button */}
                    <button
                        className="doors-fullscreen-close"
                        onClick={(e) => { e.stopPropagation(); setFullscreenImage(null); }}
                    >
                        <svg width="18" height="18" viewBox="0 0 14 14" fill="none">
                            <path d="M13 1L1 13M1 1L13 13" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
                        </svg>
                    </button>

                    {/* Back button */}
                    <button
                        className="doors-fullscreen-back"
                        onClick={(e) => { e.stopPropagation(); setFullscreenImage(null); }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                        Back
                    </button>

                    <img
                        src={fullscreenImage}
                        alt="Fullscreen door"
                        className="doors-fullscreen-img"
                        onClick={(e) => e.stopPropagation()}
                        draggable={false}
                    />
                </div>
            )}

            {/* ══════════════════════════════════════════ STYLES ══════════════════════════════════════════ */}
            <style>{`
                /* ── Base ── */
                .page-title { font-size: 46px; }
                .section-title { font-size: 32px; }
                .door-img { height: 300px; }
                .door-card:hover { transform: translateY(-8px); box-shadow: 0 15px 35px rgba(0,0,0,0.1) !important; border-color: #e5e5e5 !important; }

                /* ══════════════════════════════════════
                   UNIFIED STICKY CONTROL BAR
                ══════════════════════════════════════ */
   .sticky-control-bar {
    position: sticky;
    position: -webkit-sticky;
    top: 70px;
    z-index: 100;
    background-color: rgba(249, 241, 231, 0.96);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    padding: 20px 12px 8px 8px;
    border-bottom: 1px solid rgba(212, 175, 55, 0.2);
    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
    width: 100%;
}

                .unified-controls-row {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-wrap: wrap;
                    gap: 10px;
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 0 20px;
                }
                    .desktop-filters {
    flex: unset !important;
}

                /* Search */
                .search-wrapper {
                    position: relative;
                    width: 220px;
                    flex-shrink: 0;
                }
                .search-icon {
                    position: absolute;
                    left: 14px;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 15px;
                    height: 15px;
                    color: #999;
                    pointer-events: none;
                }
                .search-input {
                    width: 100%;
                    padding: 9px 32px 9px 38px;
                    font-size: 13px;
                    font-family: 'Poppins', sans-serif;
                    border-radius: 40px;
                    border: 1.5px solid #e0d5c1;
                    background-color: #fff;
                    outline: none;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.03);
                    transition: all 0.3s ease;
                    box-sizing: border-box;
                }
                .search-input:focus {
                    border-color: #D4AF37;
                    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.15);
                }
                .search-clear-btn {
                    position: absolute;
                    right: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: #aaa;
                    display: flex;
                    align-items: center;
                    padding: 0;
                    line-height: 1;
                }
                .search-clear-btn:hover { color: #666; }

                /* Desktop filter pills */
                .desktop-filters {
                    flex: 1;
                    overflow-x: auto;
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                .desktop-filters::-webkit-scrollbar { display: none; }
                .filter-track {
                    display: flex;
                    gap: 8px;
                    width: max-content;
                    padding: 2px;
                }
                .filter-btn {
                    padding: 7px 18px;
                    font-size: 13px;
                    border-radius: 30px;
                    border: 1px solid #D4AF37;
                    background-color: #fff;
                    color: #D4AF37;
                    cursor: pointer;
                    font-weight: 600;
                    font-family: 'Poppins', sans-serif;
                    transition: all 0.3s ease;
                    white-space: nowrap;
                }
                .filter-btn:hover { box-shadow: 0 3px 8px rgba(212, 175, 55, 0.2); }
                .filter-btn.active {
                    background-color: #D4AF37;
                    color: #fff;
                    box-shadow: 0 3px 12px rgba(212, 175, 55, 0.3);
                }

                /* Mobile-only elements hidden on desktop */
                .mobile-only { display: none !important; }

                /* Mobile Dropdown */
                .mobile-dropdown-wrapper {
                    position: relative;
                    flex-shrink: 0;
                }
                .mobile-dropdown-toggle {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 8px;
                    padding: 9px 16px;
                    font-size: 13px;
                    font-family: 'Poppins', sans-serif;
                    font-weight: 600;
                    border-radius: 30px;
                    border: 1.5px solid #D4AF37;
                    background-color: #fff;
                    color: #D4AF37;
                    cursor: pointer;
                    outline: none;
                    white-space: nowrap;
                    min-width: 100px;
                    transition: all 0.3s ease;
                    box-sizing: border-box;
                }
                .mobile-dropdown-toggle:focus {
                    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.2);
                }
                .dropdown-arrow {
                    width: 12px;
                    height: 12px;
                    color: #D4AF37;
                    transition: transform 0.3s ease;
                }
                .dropdown-arrow.open {
                    transform: rotate(180deg);
                }
                
                .mobile-dropdown-menu {
                    position: absolute;
                    top: calc(100% + 6px);
                    left: 0;
                    min-width: 150px;
                    background: #fff;
                    border: 1.5px solid #e0d5c1;
                    border-radius: 14px;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
                    z-index: 150;
                    padding: 6px;
                    overflow: hidden;
                    animation: dropdownFadeIn 0.2s ease forwards;
                    transform-origin: top left;
                }
                @keyframes dropdownFadeIn {
                    from { opacity: 0; transform: scale(0.96) translateY(-4px); }
                    to { opacity: 1; transform: scale(1) translateY(0); }
                }
                
                .mobile-dropdown-item {
                    padding: 10px 14px;
                    font-size: 13px;
                    font-family: 'Poppins', sans-serif;
                    font-weight: 500;
                    color: #5D4037;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    text-align: left;
                }
                .mobile-dropdown-item:hover {
                    background-color: rgba(212, 175, 55, 0.1);
                    color: #D4AF37;
                }
                .mobile-dropdown-item.active {
                    background-color: #D4AF37;
                    color: #fff;
                    font-weight: 600;
                }

                /* Grid Toggle */
                .grid-toggle-wrapper {
                    display: flex;
                    gap: 3px;
                    flex-shrink: 0;
                    background: rgba(255,255,255,0.7);
                    border: 1.5px solid #e0d5c1;
                    border-radius: 30px;
                    padding: 3px;
                }
                .grid-toggle-btn {
                    display: flex;
                    align-items: center;
                    gap: 3px;
                    padding: 5px 9px;
                    border: none;
                    border-radius: 24px;
                    background: transparent;
                    color: #999;
                    cursor: pointer;
                    font-size: 11px;
                    font-weight: 700;
                    font-family: 'Poppins', sans-serif;
                    transition: all 0.25s ease;
                }
                .grid-toggle-btn.active {
                    background-color: #D4AF37;
                    color: #fff;
                    box-shadow: 0 2px 8px rgba(212, 175, 55, 0.35);
                }

                .mobile-view-more-grid-item {
                    display: none !important;
                }
                .desktop-view-more-container {
                    display: block !important;
                }

                /* ── View More Button ── */
                .view-more-btn {
                    padding: 12px 35px;
                    background-color: transparent;
                    border: 1.5px solid #5D4037;
                    color: #5D4037;
                    border-radius: 30px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.35s cubic-bezier(0.165, 0.84, 0.44, 1);
                    font-family: 'Poppins', sans-serif;
                    box-shadow: 0 4px 12px rgba(93, 64, 55, 0.05);
                }
                .view-more-btn:hover { 
                    background-color: #5D4037; 
                    color: #fff;
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(93, 64, 55, 0.15);
                }

                /* ── Desktop Grid ── */
                .door-grid {
                    display: grid;
                    grid-template-columns: repeat(5, 1fr);
                    gap: 25px;
                }

                /* ══════════════════════════════════════
                   MODAL OVERLAY
                ══════════════════════════════════════ */
                .modal-overlay {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background-color: rgba(0,0,0,0.85);
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 3000;
                    padding: 40px;
                }

                /* ── Close button: FIXED so always visible even on scroll ── */
                .modal-close-btn {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    width: 45px;
                    height: 45px;
                    background: #fff;
                    border: none;
                    border-radius: 50%;
                    cursor: pointer;
                    z-index: 4000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.25);
                    transition: background 0.2s ease, transform 0.2s ease;
                }
                .modal-close-btn:hover {
                    background: #f5f5f5;
                    transform: scale(1.08);
                }

                .modal-content-container {
                    background-color: #fff;
                    max-width: 1100px;
                    width: 100%;
                    display: flex;
                    flex-direction: row;
                    border-radius: 20px;
                    overflow: hidden;
                    box-shadow: 0 30px 60px rgba(0,0,0,0.5);
                    position: relative;
                    /* ── CHANGED: fixed height on desktop so details section can scroll ── */
                    height: 82vh;
                    max-height: 88vh;
                }

                .door-description {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    max-height: 120px;
}

.door-description.expanded {
    -webkit-line-clamp: unset;
    max-height: 500px;
    overflow-y: auto;
    padding-right: 6px;
}

/* Professional scrollbar */
.door-description.expanded::-webkit-scrollbar {
    width: 5px;
}

.door-description.expanded::-webkit-scrollbar-thumb {
    background: #D4AF37;
    border-radius: 10px;
}

                /* Image section */
                .image-section {
                    flex: 1.2;
                    background-color: #F7F7F7;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 60px 30px 60px;
                    position: relative;
                    -webkit-transform: translateZ(0);
                    transform: translateZ(0);
                }

                /* ══════════════════════════════════════
                   DOOR OPEN ANIMATION
                ══════════════════════════════════════ */
                .door-perspective-wrapper {
                    width: 100%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    overflow: hidden;
                }

                .door-open-panel {
                    perspective: 900px;
                    transform-style: preserve-3d;
                    transform-origin: left center;
                    transform: perspective(900px) rotateY(-75deg);
                    opacity: 0;
                    transition:
                        transform 0.75s cubic-bezier(0.23, 1, 0.32, 1),
                        opacity 0.4s ease;
                    width: 100%;
                }

                .door-open-panel.door-opened {
                    transform: perspective(900px) rotateY(0deg);
                    opacity: 1;
                }

                .door-open-panel.door-opened {
                    animation: flattenAfterOpen 0s 0.85s forwards;
                }
                @keyframes flattenAfterOpen {
                    to { transform: none; transform-style: flat; }
                }

                /* ── Image pan wrapper ── */
                .image-pan-wrapper {
                    width: 100%;
                    overflow: hidden;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 60vh;
                    border-radius: 8px;
                    user-select: none;
                    -webkit-user-select: none;
                }

                /* ── Zoom Controls ── */
                .modal-unified-controls-row {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 12px;
                    margin-top: 16px;
                    width: 100%;
                }
                .zoom-controls {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(255, 255, 255, 0.9);
                    border: 1px solid #e0d5c1;
                    border-radius: 30px;
                    padding: 5px 8px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.08);
                }
                .zoom-3d-toggle-btn {
                    background: #fff;
                    border: 1.5px solid ${colors.gold};
                    border-radius: 30px;
                    height: 42px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding: 0 16px;
                    cursor: pointer;
                    color: ${colors.gold};
                    font-weight: 700;
                    font-size: 13px;
                    font-family: 'Poppins', sans-serif;
                    box-shadow: 0 4px 12px rgba(212, 175, 55, 0.25);
                    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }
                .zoom-3d-toggle-btn:hover {
                    transform: scale(1.05);
                    background: rgba(212, 175, 55, 0.05);
                    box-shadow: 0 6px 16px rgba(212, 175, 55, 0.35);
                }
                .zoom-btn {
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: #5D4037;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 30px;
                    height: 30px;
                    border-radius: 50%;
                    transition: background 0.2s ease;
                    padding: 0;
                }
                .zoom-btn:hover:not(:disabled) { background: rgba(212,175,55,0.15); }
                .zoom-btn:disabled { opacity: 0.35; cursor: not-allowed; }
                .zoom-reset-btn {
                    background: none;
                    border: 1px solid #e0d5c1;
                    border-radius: 20px;
                    padding: 3px 12px;
                    cursor: pointer;
                    font-size: 12px;
                    font-weight: 700;
                    font-family: 'Poppins', sans-serif;
                    color: #5D4037;
                    transition: all 0.2s ease;
                    min-width: 48px;
                    text-align: center;
                }
                .zoom-reset-btn:hover { background: rgba(212,175,55,0.15); border-color: #D4AF37; }

                /* ── Details section: flex column, fills modal height ── */
                .details-section {
                    padding: 0;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }

                /* Header: PREMIUM SERIES + Type + Model — always visible, never scrolls */
                .details-header {
                    padding: 50px 60px 25px 60px;
                    flex-shrink: 0;
                }

                /* Body: description + sizes + button — scrolls when content overflows */
                .details-body {
                    flex: 1;
                    overflow-y: auto;
                    padding: 0 60px 50px 60px;
                    scrollbar-width: thin;
                    scrollbar-color: #D4AF37 #f0ece4;
                }
                .details-body::-webkit-scrollbar { width: 5px; }
                .details-body::-webkit-scrollbar-track { background: #f0ece4; border-radius: 10px; }
                .details-body::-webkit-scrollbar-thumb { background: #D4AF37; border-radius: 10px; }

                .modal-title { font-size: 2.8rem; }
                .modal-image {
                    width: 100%;
                    max-height: 100%;
                    object-fit: contain;
                    filter: drop-shadow(10px 20px 40px rgba(0,0,0,0.15));
                    display: block;
                    image-rendering: -webkit-optimize-contrast;
                    image-rendering: crisp-edges;
                }
                .quote-btn {
    width: 100%;
    min-height: 56px;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
}

.quote-btn:hover {
    background: #c59f25 !important;
    transform: translateY(-2px);
    box-shadow: 0 12px 25px rgba(212,175,55,0.35);
}

                /* ══════════════════════════════════════
                   RESPONSIVE MEDIA QUERIES
                ══════════════════════════════════════ */
                @media (max-width: 1200px) {
                    .door-grid { grid-template-columns: repeat(4, 1fr); }
                }

                @media (max-width: 992px) {
                    .door-grid { grid-template-columns: repeat(3, 1fr); }
                    .modal-title { font-size: 2.2rem; }
                    .image-section { padding: 40px; }
                    .details-header { padding: 40px 40px 20px 40px; }
                    .details-body { padding: 0 40px 40px 40px; }
                    .search-wrapper { width: 180px; }
                }

                @media (max-width: 768px) {
                    .mobile-only { display: flex !important; }
                    .desktop-filters { display: none !important; }
                    .search-wrapper { width: auto; flex: 1; min-width: 0; }

                    .section-title { font-size: 24px !important; margin-bottom: 20px !important; }
                    .page-title { font-size: 32px !important; }

                    .door-grid.mobile-grid-2 {
                        grid-template-columns: repeat(2, 1fr) !important;
                        gap: 12px;
                    }
                    .door-grid.mobile-grid-4 {
                        grid-template-columns: repeat(4, 1fr) !important;
                        gap: 8px;
                    }
                    .mobile-grid-2 .door-img { height: 180px; }
                    .mobile-grid-4 .door-img { height: 90px; }
                    .mobile-grid-4 .door-card {
                        padding: 8px !important;
                        border-radius: 10px !important;
                    }
                    .mobile-grid-4 .door-card h4 { font-size: 11px !important; margin: 3px 0 !important; }
                    .mobile-grid-4 .door-card p  { font-size: 10px !important; }

                    /* Modal mobile */
                    .modal-overlay { padding: 0; align-items: stretch; }
                    .modal-content-container {
                        flex-direction: column !important;
                        height: 100%;
                        max-height: 100%;
                        overflow-y: auto;
                        border-radius: 0;
                        width: 100%;
                        max-width: 100%;
                    }
                    /* On mobile the whole container scrolls; disable inner scroll */
                    .details-section { overflow: visible !important; }
                    .details-body {
                        overflow-y: visible !important;
                        flex: none !important;
                    }
                    .details-header { padding: 20px 20px 15px 20px !important; }
                    .details-body { padding: 0 20px 20px 20px !important; }
                    .modal-close-btn { top: 12px; right: 12px; width: 38px; height: 38px; }
                    .image-section { padding: 30px 20px 10px 20px !important; }
                    .modal-title { font-size: 1.8rem !important; }
                    .image-pan-wrapper { height: 38vh; }
                    .quote-btn { padding: 15px; font-size: 1rem; }

                    .sticky-control-bar { padding: 20px 10px 8px 8px; margin-bottom: 25px; }
                    .unified-controls-row { gap: 8px; padding: 0 12px; }

                    .mobile-view-more-grid-item {
                        display: flex !important;
                        width: 100%;
                        height: 100%;
                    }
                    .desktop-view-more-container {
                        display: none !important;
                    }
                    .mobile-grid-view-more-btn {
                        width: 100%;
                        height: 100%;
                        border: 1.5px solid rgba(93, 64, 55, 0.16) !important;
                        border-radius: 16px !important;
                        background: #ffffff !important;
                        color: #5D4037 !important;
                        font-weight: 700 !important;
                        cursor: pointer;
                        display: flex !important;
                        flex-direction: column !important;
                        align-items: center;
                        justify-content: center;
                        gap: 8px;
                        transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1) !important;
                        text-align: center;
                        font-family: 'Poppins', sans-serif;
                        box-sizing: border-box;
                        box-shadow: 0 4px 15px rgba(93, 64, 55, 0.03) !important;
                    }
                    .mobile-grid-view-more-btn span {
                        font-size: 0.92rem;
                        line-height: 1.4;
                    }
                    .view-more-arrow-icon {
                        transition: transform 0.3s ease;
                        color: #D4AF37;
                        margin-top: 4px !important;
                    }
                    .door-grid.mobile-grid-2 .mobile-grid-view-more-btn {
                        min-height: 180px;
                        padding: 20px !important;
                    }
                    .door-grid.mobile-grid-4 .mobile-grid-view-more-btn {
                        min-height: 100px;
                        font-size: 10px !important;
                        padding: 8px !important;
                        border-radius: 12px !important;
                        gap: 4px;
                    }
                    .door-grid.mobile-grid-4 .mobile-grid-view-more-btn span {
                        font-size: 0.65rem;
                    }
                    .door-grid.mobile-grid-4 .mobile-grid-view-more-btn .view-more-arrow-icon {
                        width: 14px !important;
                        height: 14px !important;
                        margin-top: 2px !important;
                    }
                    .mobile-grid-view-more-btn:hover {
                        background-color: #5D4037 !important;
                        color: #fff !important;
                        border-color: #5D4037 !important;
                        transform: translateY(-6px);
                        box-shadow: 0 12px 24px rgba(93, 64, 55, 0.18) !important;
                    }
                    .mobile-grid-view-more-btn:hover .view-more-arrow-icon {
                        transform: translateY(4px);
                        color: #fff !important;
                    }
                    .hide-on-mobile-2x2 {
                        display: none !important;
                    }
                    .show-on-mobile-4x4 {
                        display: block !important;
                    }
                }

                @media (max-width: 480px) {
                    .page-title { font-size: 28px !important; }
                    .mobile-grid-2 .door-img { height: 160px; }
                    .mobile-grid-4 .door-img { height: 75px; }
                    .search-input { font-size: 12px !important; padding: 8px 28px 8px 32px !important; }
                    .mobile-dropdown-toggle { font-size: 12px !important; min-width: 80px; padding: 8px 12px !important; }
                    .grid-toggle-btn { padding: 4px 7px; font-size: 10px; }
                    .image-pan-wrapper { height: 32vh; }
                }

                /* ══════════════════════════════════════
                   TAP HINT — mobile only
                ══════════════════════════════════════ */
                .doors-tap-hint {
                    display: none;
                    text-align: center;
                    font-size: 11px;
                    color: #aaa;
                    margin-top: 8px;
                    font-family: 'Poppins', sans-serif;
                }
                @media (max-width: 768px) {
                    .doors-tap-hint { display: block; }
                }

                /* ══════════════════════════════════════
                   FULLSCREEN IMAGE OVERLAY
                ══════════════════════════════════════ */
                .doors-fullscreen-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.96);
                    z-index: 9999;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    animation: doors-fs-in 0.22s ease;
                }
                @keyframes doors-fs-in {
                    from { opacity: 0; transform: scale(0.96); }
                    to   { opacity: 1; transform: scale(1); }
                }

                /* Close × — top right */
                .doors-fullscreen-close {
                    position: fixed;
                    top: 16px;
                    right: 16px;
                    width: 42px;
                    height: 42px;
                    border-radius: 50%;
                    border: none;
                    background: rgba(255,255,255,0.15);
                    backdrop-filter: blur(6px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    z-index: 10001;
                    transition: background 0.2s ease, transform 0.2s ease;
                }
                .doors-fullscreen-close:hover { background: rgba(255,255,255,0.25); transform: scale(1.08); }

                /* ← Back — top left */
                .doors-fullscreen-back {
                    position: fixed;
                    top: 16px;
                    left: 16px;
                    height: 42px;
                    padding: 0 16px;
                    border-radius: 30px;
                    border: none;
                    background: rgba(255,255,255,0.15);
                    backdrop-filter: blur(6px);
                    color: #fff;
                    font-size: 14px;
                    font-weight: 700;
                    font-family: 'Poppins', sans-serif;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    cursor: pointer;
                    z-index: 10001;
                    transition: background 0.2s ease;
                }
                .doors-fullscreen-back:hover { background: rgba(255,255,255,0.25); }

                /* The fullscreen image */
                .doors-fullscreen-img {
                    max-width: 100vw;
                    max-height: 100vh;
                    width: auto;
                    height: auto;
                    object-fit: contain;
                    display: block;
                    user-select: none;
                    -webkit-user-drag: none;
                    filter: drop-shadow(0 0 40px rgba(255,255,255,0.08));
                }
            `}</style>
        </div>
    );
};

export default Doors;
